// Page /collab/activate/:token — Lot 15.
// Activation d'une invitation collaborateur (mot de passe + auto-login).

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2, CheckCircle2, AlertCircle, Lock } from "lucide-react";
import Logo from "@/components/Logo";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ActivateInvitation = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { refreshMe } = useAuth();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [busy, setBusy] = useState(false);
  const [activated, setActivated] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const inv = await api.peekInvitation(token);
        setInvite(inv);
      } catch (e) {
        setError(e.message || "Invitation invalide ou expirée.");
      } finally { setLoading(false); }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) return setError("8 caractères minimum.");
    if (password !== password2) return setError("Les mots de passe ne correspondent pas.");
    setBusy(true);
    try {
      await api.activateInvitation(token, password);
      setActivated(true);
      // Synchronise le contexte auth AVANT la redirection (fix E2E Lot 15)
      try { await refreshMe?.(); } catch { /* silencieux */ }
      setTimeout(() => navigate("/app", { replace: true }), 900);
    } catch (err) { setError(err.message || "Erreur"); }
    finally { setBusy(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F8FA] flex items-center justify-center">
        <Loader2 size={20} className="animate-spin" />
      </div>
    );
  }

  if (!invite) {
    return (
      <div className="min-h-screen bg-[#F7F8FA] flex items-center justify-center p-6">
        <div className="bg-white rounded-3xl p-8 max-w-md w-full text-center border border-black/5">
          <div className="mx-auto h-12 w-12 rounded-full bg-[#FEE2E2] flex items-center justify-center">
            <AlertCircle size={22} className="text-[#991B1B]" />
          </div>
          <h1 className="mt-4 text-xl font-semibold text-[#111]">Invitation invalide</h1>
          <p className="mt-2 text-[13.5px] text-[#6B7280]">
            Ce lien n'est plus valable. Demandez à un administrateur de votre
            agence de vous renvoyer une invitation.
          </p>
        </div>
      </div>
    );
  }

  if (activated) {
    return (
      <div className="min-h-screen bg-[#F7F8FA] flex items-center justify-center p-6">
        <div data-testid="activation-success" className="bg-white rounded-3xl p-8 max-w-md w-full text-center border border-black/5">
          <div className="mx-auto h-12 w-12 rounded-full bg-[#D9FFE8] flex items-center justify-center">
            <CheckCircle2 size={22} className="text-[#166534]" />
          </div>
          <h1 className="mt-4 text-xl font-semibold text-[#111]">Compte activé</h1>
          <p className="mt-2 text-[13.5px] text-[#6B7280]">
            Bienvenue chez {invite.agency_name}. Redirection en cours…
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F8FA] flex items-center justify-center p-6">
      <div data-testid="page-activate-invitation"
           className="bg-white rounded-3xl p-8 max-w-md w-full border border-black/5">
        <Logo variant="full" size="sm" rounded="rounded-md" />
        <p className="mt-6 text-[12px] font-semibold uppercase tracking-widest text-[#6B7280]">
          Invitation
        </p>
        <h1 className="mt-2 text-2xl font-semibold text-[#111] tracking-tight">
          Bienvenue chez {invite.agency_name}
        </h1>
        <p className="mt-2 text-[13.5px] text-[#6B7280] leading-relaxed">
          <b>{(invite.first_name || "") + " " + (invite.last_name || "")}</b> — vous rejoignez
          l'équipe en tant que <b>{invite.role_label}</b>. Choisissez un mot de passe pour finaliser
          votre compte.
        </p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <label className="text-[12px] font-semibold text-[#111]">Email</label>
            <input value={invite.email} readOnly
                   className="mt-1.5 w-full h-11 rounded-xl border border-black/10 bg-[#F7F8FA] px-4 text-[14px] text-[#6B7280]" />
          </div>
          <div>
            <label className="text-[12px] font-semibold text-[#111]">Nouveau mot de passe</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                   required minLength={8}
                   data-testid="activate-password"
                   className="mt-1.5 w-full h-11 rounded-xl border border-black/10 bg-white px-4 text-[14px] outline-none focus:border-[#111]" />
            <p className="mt-1 text-[11.5px] text-[#9CA3AF]">Minimum 8 caractères.</p>
          </div>
          <div>
            <label className="text-[12px] font-semibold text-[#111]">Confirmer</label>
            <input type="password" value={password2} onChange={(e) => setPassword2(e.target.value)}
                   required
                   data-testid="activate-password2"
                   className="mt-1.5 w-full h-11 rounded-xl border border-black/10 bg-white px-4 text-[14px] outline-none focus:border-[#111]" />
          </div>
          {error && (
            <div className="rounded-lg bg-[#FEE2E2] text-[#991B1B] text-[12.5px] px-3 py-2">{error}</div>
          )}
          <button type="submit" disabled={busy}
                  data-testid="activate-submit"
                  className="w-full h-12 rounded-full bg-[#111] text-white text-[14px] font-medium hover:bg-black disabled:opacity-50">
            {busy ? <Loader2 size={15} className="animate-spin inline" /> : (<><Lock size={14} className="inline -mt-0.5 mr-1.5" /> Activer mon compte</>)}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ActivateInvitation;
